import { z } from 'zod';
import { PapelSchema } from '../comum/papeis';
import { EmailSchema, SenhaSchema } from '../auth';
import { ProjetoChaveSchema } from '../projetos';

export const CriarUsuarioSchema = z.object({
  email: EmailSchema,
  nome: z.string().min(2).max(120).trim(),
  senha: SenhaSchema,
  papel: PapelSchema,
});
export type CriarUsuario = z.infer<typeof CriarUsuarioSchema>;

export const AtualizarUsuarioSchema = z
  .object({
    nome: z.string().min(2).max(120).trim().optional(),
    papel: PapelSchema.optional(),
    ativo: z.boolean().optional(),
    senha: SenhaSchema.optional(),
    /** Exige o segundo fator desta conta. ADMIN exige sempre, com ou sem a flag. */
    totpExigido: z.boolean().optional(),
    /** Um aparelho por vez: entrar derruba o que a conta tinha vivo (ADR-0020). */
    umAparelhoPorVez: z.boolean().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, 'informe ao menos um campo');
export type AtualizarUsuario = z.infer<typeof AtualizarUsuarioSchema>;

export const UsuarioSchema = z.object({
  id: z.string(),
  email: z.string(),
  nome: z.string(),
  papel: PapelSchema,
  totpAtivo: z.boolean(),
  totpExigido: z.boolean(),
  /**
   * Um aparelho por vez (ADR-0020). Nasce FALSE: o padrão da casa passou a ser
   * poder entrar em vários navegadores. A capacidade de derrubar continua
   * inteira, agora como escolha por conta — é o que serve no dia em que
   * alguém compartilhar senha.
   */
  umAparelhoPorVez: z.boolean(),
  ativo: z.boolean(),
  // O eixo por usuário do ADR-0019, na listagem para a tela de marcação.
  verTudo: z.boolean(),
  verMestreClaude: z.boolean(),
  verMestreCodex: z.boolean(),
  /** A leitura da mestre do terceiro motor (F16, D24): a mesma regra das duas de cima. */
  verMestreOpencode: z.boolean(),
  criadoEm: z.iso.datetime(),
});
export type Usuario = z.infer<typeof UsuarioSchema>;

/**
 * Resposta de `GET /usuarios/:id/visibilidade` (ADR-0019, revisto pelo
 * ADR-0020): as três flags e os PROJETOS liberados para aquela conta.
 *
 * `sessoes` sumiu daqui de propósito. A marcação por sessão continua
 * existindo, mas virou EXCEÇÃO e mudou de casa: quem a edita é o painel da
 * própria conversa (`GET/PUT /sessoes/:id/visibilidade`). Uma tela de contas
 * com 192 caixinhas — o número medido em 2026-09-21 — não é uma tela.
 */
export const VisibilidadeUsuarioSchema = z.object({
  verTudo: z.boolean(),
  verMestreClaude: z.boolean(),
  verMestreCodex: z.boolean(),
  /** F16, D24: a flag da mestre do OpenCode, restrita a OPERADOR+ como as outras. */
  verMestreOpencode: z.boolean(),
  projetos: z.array(ProjetoChaveSchema),
});
export type VisibilidadeUsuario = z.infer<typeof VisibilidadeUsuarioSchema>;

/**
 * Corpo do `PUT /usuarios/:id/visibilidade`: as três flags e a lista COMPLETA
 * de projetos liberados — o servidor faz o diff (apaga o que saiu, cria o que
 * entrou). Lista completa, e não delta, para dois admins salvando quase ao
 * mesmo tempo convergirem no que o último viu, em vez de somarem restos.
 *
 * O teto é 500, e não 2000: projeto é (máquina, pasta), e a frota inteira tem
 * 45 pares distintos. Um corpo maior que isso não é uso, é engano.
 */
export const AtualizarVisibilidadeUsuarioSchema = z.object({
  verTudo: z.boolean(),
  verMestreClaude: z.boolean(),
  verMestreCodex: z.boolean(),
  /**
   * F16, D24. **Opcional, e ausente é "não mexe"** — e não `false`: um padrão
   * aqui faria o PUT de uma tela que ainda não conhece a caixa APAGAR a flag
   * que outro admin ligou, calado. Presente, vale a regra das outras duas
   * (recusada para LEITOR com `flag_de_mestre_exige_operador`).
   */
  verMestreOpencode: z.boolean().optional(),
  projetos: z.array(ProjetoChaveSchema).max(500),
});
export type AtualizarVisibilidadeUsuario = z.infer<typeof AtualizarVisibilidadeUsuarioSchema>;

/**
 * A EXCEÇÃO por sessão, nos dois sentidos (ADR-0020).
 *
 * `PERMITIR` abre uma conversa de um projeto que a conta não tem; `NEGAR`
 * esconde uma conversa de um projeto que ela tem. Ausência da linha é
 * "vale a regra do projeto" — que é diferente de `PERMITIR`, e a diferença
 * importa: apagar a exceção devolve a sessão ao destino do projeto, e é assim
 * que se desfaz um engano sem ter de adivinhar qual era o estado anterior.
 */
export const SentidoDoAcessoSchema = z.enum(['PERMITIR', 'NEGAR']);
export type SentidoDoAcesso = z.infer<typeof SentidoDoAcessoSchema>;

/** Uma conta na tela "quem vê esta conversa", com o efeito já resolvido. */
export const EspectadorDaSessaoSchema = z.object({
  usuarioId: z.string(),
  nome: z.string(),
  email: z.string(),
  papel: PapelSchema,
  /** O veredito FINAL, com projeto, dono, flags e exceção já combinados. */
  ve: z.boolean(),
  /** Nulo = sem exceção: esta conta segue a regra do projeto. */
  excecao: SentidoDoAcessoSchema.nullable(),
  /**
   * Por que ela vê (ou não) ANTES da exceção — é o que deixa a tela explicar
   * "já via pelo projeto" em vez de só mostrar uma caixa marcada.
   */
  motivoBase: z.enum(['ADMIN', 'VER_TUDO', 'DONO', 'PROJETO', 'MESTRE_POR_FLAG', 'NENHUM']),
});
export type EspectadorDaSessao = z.infer<typeof EspectadorDaSessaoSchema>;

/** Resposta de `GET /sessoes/:id/visibilidade` — só ADMIN. */
export const VisibilidadeDaSessaoSchema = z.object({
  sessaoId: z.string(),
  /** O projeto a que esta conversa pertence, para a tela dizer o que é a base. */
  projeto: ProjetoChaveSchema,
  espectadores: z.array(EspectadorDaSessaoSchema),
});
export type VisibilidadeDaSessao = z.infer<typeof VisibilidadeDaSessaoSchema>;

/**
 * Corpo do `PUT /sessoes/:id/visibilidade`: as exceções desta conversa, lista
 * completa. Conta que não aparecer aqui fica SEM exceção — volta a seguir o
 * projeto. É o gesto de "desfazer" escrito no próprio formato.
 */
export const AtualizarVisibilidadeDaSessaoSchema = z.object({
  excecoes: z
    .array(z.object({ usuarioId: z.string().min(1), sentido: SentidoDoAcessoSchema }))
    .max(500),
});
export type AtualizarVisibilidadeDaSessao = z.infer<typeof AtualizarVisibilidadeDaSessaoSchema>;
